import {
  Block,
  BlockRelationship,
  CommonBRsCompoundIndexes,
  RootBlock,
  commonBRsCompoundIndexes,
  getOnIndexConflictHandler,
  isBRInRoot,
  isRootBlock,
} from '@basehub/data/replicache/index'
import { IndexedMap, createIndexedMap } from '@basehub/utils/indexed-map/index'
import { invariant } from '@epic-web/invariant'
import { create } from 'zustand'

export const useBlocksMapStore = create<{
  blocksMap: IndexedMap<Block>
  brsMap: IndexedMap<BlockRelationship>
  _brsByRootId: IndexedMap<
    IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
  >
  getBrsByRootId: (
    rootBlock: RootBlock
  ) => IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
  recentlyDeleted: Map<string, string>
}>((_set, get) => ({
  /**
   * Non reactive map of all blocks and brs in local cache.
   */
  blocksMap: createIndexedMap(),
  brsMap: createIndexedMap({
    onChange: ({ item: br, operation }) => {
      const store = get()

      for (const rootId of store._brsByRootId.keys()) {
        const existingIndex = store._brsByRootId.get(rootId)
        const existingRoot = store.blocksMap.get(rootId)
        invariant(
          existingRoot && isRootBlock(existingRoot),
          `root ${rootId} not found`
        )
        if (isBRInRoot(br, existingRoot)) {
          if (operation === 'upsert') {
            // If we have an existing index for this root, update it
            if (existingIndex) {
              existingIndex.set(br.id, br)
            }
            // Ignore - index will be created on first access
          } else if (operation === 'delete') {
            // If we have an existing index, remove the BR from it
            existingIndex?.delete(br.id)
          }
        } else {
          existingIndex?.delete(br.id)
        }
      }
    },
  }),
  _brsByRootId: createIndexedMap(),
  recentlyDeleted: new Map(),
  getBrsByRootId: (rootBlock) => {
    const bStore = get()
    const existing = bStore._brsByRootId.get(rootBlock.id)
    if (existing) {
      return existing
    }
    const newMap = createIndexedMap<
      BlockRelationship,
      CommonBRsCompoundIndexes
    >({
      autoFallbackKeys: ['sourceBlockId', 'blockId'],
      compoundIndexes: commonBRsCompoundIndexes,
      onIndexConflict: getOnIndexConflictHandler(() => rootBlock),
    })
    for (const br of bStore.brsMap.values()) {
      if (isBRInRoot(br, rootBlock)) {
        newMap.set(br.id, br)
      }
    }
    bStore._brsByRootId.set(rootBlock.id, newMap)
    return newMap
  },
}))
